import { useState } from "react";
import { Mail, MapPin, Phone, Check, AlertCircle } from "lucide-react";
import { submitContact } from "../lib/api";

const INITIAL = { full_name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: null, message: "" });
    try {
      await submitContact(form);
      setStatus({ type: "success", message: "Votre message a bien été envoyé. Nous vous répondrons dans les meilleurs délais." });
      setForm(INITIAL);
    } catch (err) {
      setStatus({ type: "error", message: "Une erreur est survenue. Merci de réessayer." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32 pb-20 px-6 md:px-12 lg:px-24 min-h-screen">
      <header className="mb-20 max-w-4xl">
        <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-6">
          Nous écrire
        </p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.05] tracking-tight font-light mb-8">
          Nous <em className="text-[#2C402E]">contacter</em>
        </h1>
        <p className="text-lg text-[#4A4A4A] font-body leading-relaxed max-w-2xl">
          Une question, une suggestion, une demande de partenariat ? Écrivez-nous,
          nous serons ravis d'échanger avec vous.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Info */}
        <aside className="lg:col-span-4 space-y-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Mail size={18} className="text-[#9E4735]" />
              <p className="text-xs tracking-[0.2em] uppercase text-[#9E4735] font-body">Courriel</p>
            </div>
            <a href="mailto:challengeslitteraires.editions@gmail.com" data-testid="contact-email" className="font-serif text-lg text-[#1A1A1A] link-underline break-all">
              challengeslitteraires.editions@gmail.com
            </a>
            <p className="text-sm text-[#757575] font-body mt-1">Demandes générales & manuscrits</p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <MapPin size={18} className="text-[#9E4735]" />
              <p className="text-xs tracking-[0.2em] uppercase text-[#9E4735] font-body">Adresse</p>
            </div>
            <p className="font-serif text-xl text-[#1A1A1A] leading-snug">
              Kellyoh<br/>33 avenue Philippe Auguste<br/>75011 Paris<br/>France
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Phone size={18} className="text-[#9E4735]" />
              <p className="text-xs tracking-[0.2em] uppercase text-[#9E4735] font-body">Téléphone</p>
            </div>
            <p className="font-serif text-xl text-[#1A1A1A]">+33 7 80 95 12 17</p>
            <p className="text-sm text-[#757575] font-body mt-1">Du lundi au vendredi, 9h–17h</p>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-8">
          {status.type === "success" && (
            <div data-testid="contact-success" className="border border-[#2C402E] bg-[#FAF9F6] p-6 mb-8 flex gap-4">
              <Check size={20} className="text-[#2C402E] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-body text-[#1A1A1A]">{status.message}</p>
            </div>
          )}
          {status.type === "error" && (
            <div data-testid="contact-error" className="border border-[#9E4735] bg-[#FAF9F6] p-6 mb-8 flex gap-4">
              <AlertCircle size={20} className="text-[#9E4735] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-body text-[#1A1A1A]">{status.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="text-sm text-[#4A4A4A] font-body mb-2 block">Nom complet *</label>
                <input name="full_name" required value={form.full_name} onChange={handleChange} data-testid="contact-input-name"
                  className="bg-transparent border-b border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-0 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A]" />
              </div>
              <div>
                <label className="text-sm text-[#4A4A4A] font-body mb-2 block">Email *</label>
                <input name="email" type="email" required value={form.email} onChange={handleChange} data-testid="contact-input-email"
                  className="bg-transparent border-b border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-0 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A]" />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#4A4A4A] font-body mb-2 block">Sujet *</label>
              <input name="subject" required value={form.subject} onChange={handleChange} data-testid="contact-input-subject"
                className="bg-transparent border-b border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-0 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A]" />
            </div>
            <div>
              <label className="text-sm text-[#4A4A4A] font-body mb-2 block">Message *</label>
              <textarea name="message" rows={8} required value={form.message} onChange={handleChange} data-testid="contact-input-message"
                className="bg-transparent border border-[#D1CFC7] focus:border-[#2C402E] rounded-none px-4 py-3 w-full outline-none transition-colors font-body text-[#1A1A1A] resize-none" />
            </div>
            <button type="submit" disabled={submitting} data-testid="contact-submit-button"
              className="bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] disabled:bg-[#757575] px-12 py-4 font-body tracking-wide transition-all">
              {submitting ? "Envoi en cours…" : "Envoyer le message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
